import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { App as CapApp } from "@capacitor/app";
import {
  accounts as accountsApi,
  auth,
  messaging,
  kg,
  type Account,
  type Conversation,
  type Me,
} from "../api";
import {
  NetIcon,
  netInfo,
  netKey,
  isEmailNetwork,
  Avatar,
  Ic,
  fmtTime,
  previewOf,
  ComposeIcon,
} from "../cockpit-ui";
import {
  Sparkles,
  Folder,
  ScrollText,
  Settings as SettingsIcon,
  LogOut,
  Bell,
  MessagesSquare,
  Mail,
  Search,
  Contact,
  Users,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { InfoPanel } from "./InfoPanel";
import { Thread } from "./Thread";
import { EmptyState, PageLoader } from "./ui";
import { useRealtime } from "../hooks/useRealtime";
import { CommandPalette, type PaletteCommand } from "./CommandPalette";
import { applyPrefs, applyZoom, clampZoom, loadPrefs, savePrefs, type World } from "../prefs";
import { requestNotifyPermission } from "../notify";
import { getDismissedContacts, getDismissedThreadReplies } from "../notif-read";

// Écrans non critiques au démarrage → chunks séparés (React.lazy + Suspense) :
// le bundle initial reste centré sur login + liste + fil (perf premier rendu).
const Audit = lazy(() => import("./Audit").then((m) => ({ default: m.Audit })));
const Hermes = lazy(() => import("./Hermes").then((m) => ({ default: m.Hermes })));
const ComposePane = lazy(() => import("./ComposePane").then((m) => ({ default: m.ComposePane })));
const Projects = lazy(() => import("./Projects").then((m) => ({ default: m.Projects })));
const Crm = lazy(() => import("./Crm").then((m) => ({ default: m.Crm })));
const Settings = lazy(() => import("./Settings").then((m) => ({ default: m.Settings })));
const MatchCenter = lazy(() => import("./MatchCenter").then((m) => ({ default: m.MatchCenter })));
const NotificationCenter = lazy(() =>
  import("./NotificationCenter").then((m) => ({ default: m.NotificationCenter })),
);

/** A conversation reads as unread on new incoming messages OR a manual mark. */
const convUnread = (c: Conversation) => c.isUnread || c.unreadCount > 0;

// — Catégories/labels Gmail —
// Le connecteur Gmail remonte des tokens portables sur `conversation.labels` :
// "category:primary|social|promotions|updates|forums" (onglets Gmail) +
// "label:<nom>" (labels perso). On reproduit le comportement Gmail : la vue
// « Réception » ne montre que le Principal, les autres onglets/labels sont
// accessibles via des filtres dédiés.
const GMAIL_CATEGORY_LABELS: Record<string, string> = {
  primary: "Principal",
  social: "Réseaux sociaux",
  promotions: "Promotions",
  updates: "Mises à jour",
  forums: "Forums",
};
/** Onglets non-Principal, dans l'ordre d'affichage Gmail. */
const GMAIL_SECONDARY_CATEGORIES = ["social", "promotions", "updates", "forums"] as const;

/** La catégorie Gmail d'une conversation ("primary"…"forums"), ou null hors Gmail. */
const gmailCategory = (c: Conversation): string | null => {
  if (netKey(c.network) !== "gmail") return null;
  const tok = (c.labels ?? []).find((l) => l.startsWith("category:"));
  return tok ? tok.slice("category:".length) : "primary";
};
/** Une conv Gmail reléguée hors « Principal » (onglet Social/Promotions/…). */
const isSecondaryGmail = (c: Conversation): boolean => {
  const cat = gmailCategory(c);
  return cat != null && cat !== "primary";
};
/**
 * Conv Gmail dont on SAIT qu'elle est hors INBOX (labels captés mais pas de token
 * "inbox") — courrier envoyé/archivé. Exclue de « Principal » et des onglets, comme
 * Gmail. Les conv pas encore catégorisées (labels NULL/vide) ne sont PAS masquées
 * (on ne cache jamais du courrier par défaut ; le relabel les renseigne ensuite).
 */
const gmailKnownNotInbox = (c: Conversation): boolean => {
  if (netKey(c.network) !== "gmail") return false;
  const ls = c.labels ?? [];
  return ls.length > 0 && !ls.includes("inbox");
};
// Pour les e-mails, on inverse l'affichage façon boîte de réception : le TITRE est
// l'expéditeur (correspondant) et l'APERÇU est l'objet du fil — plus simple à
// suivre. Les autres réseaux gardent titre = nom de conversation, aperçu = message.
/** Titre de ligne : expéditeur pour les e-mails, sinon le titre (objet/nom). */
const rowTitle = (c: Conversation): string => {
  const fallback = c.title || netInfo(c.network).label;
  if (isEmailNetwork(c.network)) return c.lastMessageSenderName || fallback;
  return fallback;
};
/** Aperçu de ligne : objet pour les e-mails, sinon l'aperçu du dernier message. */
const rowPreview = (c: Conversation): string => {
  if (isEmailNetwork(c.network)) return c.title || previewOf(c);
  return previewOf(c);
};

/**
 * Short workspace label for a multi-account row: "Slack — Acme" → "Acme" (drop
 * the network prefix), else the full display name. Lets two Slack workspaces be
 * told apart at a glance in the conversation list.
 */
function acctShortLabel(a: Account): string {
  const dash = a.displayName.indexOf("—");
  const tail = dash >= 0 ? a.displayName.slice(dash + 1).trim() : a.displayName.trim();
  return tail || netInfo(a.network).label;
}

// Une « vue » de l'app pour l'historique précédent/suivant (#4).
type NavView =
  | { k: "home" }
  | { k: "conv"; id: string }
  | { k: "crm"; company: string | null; person: string | null }
  | { k: "settings" | "hermes" | "projects" | "audit" | "matches" | "notifs" | "compose" };

export function Shell({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [showHermes, setShowHermes] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const [showCrm, setShowCrm] = useState(false);
  // Marque demandée depuis l'InfoPanel (ouverture du CRM sur sa fiche).
  const [crmCompanyName, setCrmCompanyName] = useState<string | null>(null);
  // Contact demandé depuis les notifications (#10 : « nouveau contact → compléter »).
  const [crmPersonId, setCrmPersonId] = useState<string | null>(null);
  const [showAudit, setShowAudit] = useState(false);
  const [showMatches, setShowMatches] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [matchCount, setMatchCount] = useState(0);
  // Modèle A (#1) : identités « à confirmer » → comptées dans le badge cloche.
  const [pendingCount, setPendingCount] = useState(0);
  // Nombre de réponses de fil (suivies / mentionnées) non « vues » → badge cloche.
  const [threadNotifCount, setThreadNotifCount] = useState(0);
  // Fil à ouvrir au montage d'une conversation, depuis une notif (incr. 2).
  const [pendingThread, setPendingThread] = useState<{
    convId: string;
    rootRemoteId: string;
  } | null>(null);
  const closePanels = useCallback(() => {
    setShowSettings(false);
    setShowHermes(false);
    setShowProjects(false);
    setShowCrm(false);
    setShowAudit(false);
    setShowMatches(false);
    setShowNotifs(false);
    setShowInfo(false);
    setShowCompose(false);
  }, []);
  // Badge du centre de fusion : nombre de suggestions en attente. Rafraîchi au
  // montage, périodiquement (repli), et après chaque action via onChanged.
  const refreshMatchCount = useCallback(() => {
    kg.matchSuggestions("pending")
      .then((r) => {
        // Une fusion en attente est une ACTION (fusionner / écarter), pas une
        // notif à « lire » : on compte toutes les paires pending. Le serveur est
        // la vérité (approve/decline les sort de pending) — pas de masquage local
        // qui les ferait disparaître tout en restant à traiter.
        setMatchCount(r.suggestions.length);
      })
      .catch(() => {
        /* best-effort : le badge n'est qu'indicatif */
      });
    // Modèle A (#1) : les identités « à confirmer » comptent aussi dans le badge,
    // MAIS en excluant celles déjà ignorées localement (sinon le badge reste à 9
    // alors que le centre, qui les masque, est vide → désync badge/contenu).
    kg.pendingPersons()
      .then((r) => {
        const dismissed = getDismissedContacts();
        setPendingCount(r.pending.filter((c) => !dismissed.has(c.id)).length);
      })
      .catch(() => {
        /* best-effort */
      });
  }, []);
  // Badge cloche : réponses de fil pertinentes non « vues » localement (incr. 2).
  const refreshThreadNotifCount = useCallback(() => {
    messaging
      .threadActivity()
      .then((r) => {
        const dismissed = getDismissedThreadReplies();
        setThreadNotifCount(r.replies.filter((x) => !dismissed.has(x.id)).length);
      })
      .catch(() => {
        /* best-effort : le badge n'est qu'indicatif */
      });
  }, []);
  // Rafraîchit les deux sources du badge cloche (fusions + réponses de fil).
  const refreshNotifCounts = useCallback(() => {
    refreshMatchCount();
    refreshThreadNotifCount();
  }, [refreshMatchCount, refreshThreadNotifCount]);
  useEffect(() => {
    refreshNotifCounts();
    const t = setInterval(refreshNotifCounts, 60000);
    return () => clearInterval(t);
  }, [refreshNotifCounts]);
  // Marque lu : vide le badge en optimiste, puis envoie un VRAI accusé de lecture
  // à la plateforme (POST /read → markRead côté connecteur + fan-out SSE).
  const markConvRead = useCallback(async (id: string) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, isUnread: false, unreadCount: 0 } : c)),
    );
    try {
      await messaging.markRead(id);
    } catch {
      /* hors-ligne : le badge local est déjà vidé ; le refresh/SSE resynchronise */
    }
  }, []);
  // Ouvrir une conversation ferme tout outil ouvert (Hermes/Clients-projets/Audit/
  // Comptes) : sinon le panneau reste affiché par-dessus le fil (cf rendu de
  // <main>, qui priorise les panneaux sur `selected`). Ouvrir marque aussi la
  // conversation lue (compteur + pastille) ET propage l'accusé de lecture vers
  // la messagerie (synchro bidirectionnelle).
  const openConversation = useCallback(
    (c: Conversation) => {
      closePanels();
      setSelected(c);
      if (convUnread(c)) void markConvRead(c.id);
    },
    [closePanels, markConvRead],
  );
  // Ouvrir une conversation par son id (depuis un panneau qui n'a qu'un id, ex.
  // CRM niveau 2 #51) : on la résout dans la liste chargée ; si absente
  // (archivée/filtrée), on recharge puis on ouvre.
  const openConversationById = useCallback(
    (conversationId: string) => {
      const match = conversations.find((c) => c.id === conversationId);
      if (match) {
        openConversation(match);
        return;
      }
      messaging
        .conversations()
        .then((r) => {
          setConversations(r.conversations);
          const m = r.conversations.find((c) => c.id === conversationId);
          if (m) openConversation(m);
        })
        .catch(() => {
          /* best-effort : la conversation reste accessible depuis l'inbox */
        });
    },
    [conversations, openConversation],
  );
  // Depuis une notif de fil : ouvre la conversation puis demande l'ouverture du
  // panneau de thread (résolu côté Thread via le remoteId de la racine).
  const openThreadFromNotif = useCallback(
    (conversationId: string, rootRemoteId: string | null) => {
      openConversationById(conversationId);
      setPendingThread(rootRemoteId ? { convId: conversationId, rootRemoteId } : null);
    },
    [openConversationById],
  );
  // Aller à la racine messagerie (brique Messagerie) = inbox sans fil ouvert.
  const navHome = useCallback(() => {
    closePanels();
    setSelected(null);
  }, [closePanels]);
  // Ouvrir une brique/outil-panneau (Projets/Hermes/Réglages/Audit/Compose/CRM).
  const navPanel = useCallback(
    (
      p: "projects" | "hermes" | "settings" | "audit" | "compose" | "matches" | "notifs" | "crm",
    ) => {
      closePanels();
      if (p === "projects") setShowProjects(true);
      else if (p === "hermes") setShowHermes(true);
      else if (p === "settings") setShowSettings(true);
      else if (p === "audit") setShowAudit(true);
      else if (p === "compose") setShowCompose(true);
      else if (p === "notifs") setShowNotifs(true);
      else if (p === "crm") setShowCrm(true);
      else if (p === "matches") {
        setShowMatches(true);
        refreshMatchCount();
      }
      // CRM et Notifications n'ont rien à voir avec le fil ouvert → on le ferme
      // (contrairement à Hermes/Compose qui agissent sur la conversation courante).
      if (p === "crm" || p === "notifs") setSelected(null);
    },
    [closePanels, refreshMatchCount],
  );
  // — Historique de navigation (#4) : flèches précédent/suivant dans le header.
  // On OBSERVE la vue courante (dérivée de l'état) et on l'empile ; back/forward
  // rejoue une entrée SANS la ré-empiler (garde `applying`). Pas de refonte des
  // fonctions de nav : l'effet ci-dessous suit la vue résultante.
  const currentView = useMemo<NavView>(() => {
    if (showSettings) return { k: "settings" };
    if (showHermes) return { k: "hermes" };
    if (showProjects) return { k: "projects" };
    if (showAudit) return { k: "audit" };
    if (showMatches) return { k: "matches" };
    if (showNotifs) return { k: "notifs" };
    if (showCompose) return { k: "compose" };
    if (showCrm) return { k: "crm", company: crmCompanyName, person: crmPersonId };
    if (selected) return { k: "conv", id: selected.id };
    return { k: "home" };
  }, [
    showSettings,
    showHermes,
    showProjects,
    showAudit,
    showMatches,
    showNotifs,
    showCompose,
    showCrm,
    crmCompanyName,
    crmPersonId,
    selected,
  ]);
  const histRef = useRef<{ stack: NavView[]; i: number; applying: boolean }>({
    stack: [],
    i: -1,
    applying: false,
  });
  const [navState, setNavState] = useState({ canBack: false, canFwd: false });
  const viewKey = JSON.stringify(currentView);
  useEffect(() => {
    const h = histRef.current;
    const sync = () => setNavState({ canBack: h.i > 0, canFwd: h.i < h.stack.length - 1 });
    if (h.applying) {
      h.applying = false;
      sync();
      return;
    }
    if (h.i >= 0 && JSON.stringify(h.stack[h.i]) === viewKey) return; // doublon
    h.stack = h.stack.slice(0, h.i + 1);
    h.stack.push(JSON.parse(viewKey) as NavView);
    h.i = h.stack.length - 1;
    sync();
  }, [viewKey]);
  const applyView = useCallback(
    (v: NavView) => {
      histRef.current.applying = true;
      if (v.k === "home") navHome();
      else if (v.k === "conv") openConversationById(v.id);
      else if (v.k === "crm") {
        setCrmCompanyName(v.company);
        setCrmPersonId(v.person);
        navPanel("crm");
      } else navPanel(v.k);
    },
    [navHome, navPanel, openConversationById],
  );
  const navBack = useCallback(() => {
    const h = histRef.current;
    if (h.i <= 0) return;
    h.i -= 1;
    applyView(h.stack[h.i]!);
  }, [applyView]);
  const navForward = useCallback(() => {
    const h = histRef.current;
    if (h.i >= h.stack.length - 1) return;
    h.i += 1;
    applyView(h.stack[h.i]!);
  }, [applyView]);

  // Commandes de la palette ⌘K (navigation/actions). Les conversations sont
  // ajoutées par le composant lui-même depuis la liste.
  const paletteCommands = useMemo<PaletteCommand[]>(
    () => [
      { id: "inbox", label: "Réception", icon: <MessagesSquare size={16} />, run: navHome },
      {
        id: "notifs",
        label: "Notifications",
        icon: <Bell size={16} />,
        run: () => navPanel("notifs"),
      },
      {
        id: "compose",
        label: "Nouveau message",
        icon: <ComposeIcon size={16} />,
        run: () => navPanel("compose"),
      },
      {
        id: "crm",
        label: "Clients & contacts",
        icon: <Contact size={16} />,
        run: () => {
          setCrmCompanyName(null);
          navPanel("crm");
        },
      },
      {
        id: "projects",
        label: "Clients & projets",
        icon: <Folder size={16} />,
        run: () => navPanel("projects"),
      },
      {
        id: "hermes",
        label: "Hermes",
        icon: <Sparkles size={16} />,
        run: () => navPanel("hermes"),
      },
      {
        id: "matches",
        label: matchCount > 0 ? `Contacts à fusionner (${matchCount})` : "Contacts à fusionner",
        icon: <Users size={16} />,
        run: () => navPanel("matches"),
      },
      {
        id: "settings",
        label: "Réglages",
        icon: <SettingsIcon size={16} />,
        run: () => navPanel("settings"),
      },
      {
        id: "audit",
        label: "Journal d'audit",
        icon: <ScrollText size={16} />,
        run: () => navPanel("audit"),
      },
    ],
    [navHome, navPanel, matchCount],
  );
  const [filter, setFilter] = useState<string>("all");
  // « Monde » de la liste : chat (messagerie) ou e-mail. Les deux ne se mélangent
  // pas dans la même liste (un fil e-mail ne noie pas un ping). Restauré du storage.
  const [world, setWorld] = useState<World>(loadPrefs().world);
  const switchWorld = useCallback((w: World) => {
    setWorld(w);
    setFilter("all"); // un filtre réseau du monde précédent n'a pas de sens ici
    savePrefs({ world: w });
  }, []);
  const [searchOpen, setSearchOpen] = useState(false);
  const [accountMenu, setAccountMenu] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs());
  const updatePrefs = useCallback((patch: Partial<typeof prefs>) => {
    setPrefs((cur) => {
      const next = { ...cur, ...patch };
      savePrefs(patch);
      applyPrefs(next);
      return next;
    });
  }, []);
  // Applique les préférences d'apparence au montage (classes <body>).
  useEffect(() => {
    applyPrefs(loadPrefs());
  }, []);
  const searchRef = useRef<HTMLInputElement>(null);
  // Liste de conversations : flèches haut/bas entre les lignes, Entrée ouvre.
  function onChatItemKeys(e: React.KeyboardEvent<HTMLDivElement>, c: Conversation) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openConversation(c);
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const sib =
      e.key === "ArrowDown"
        ? e.currentTarget.nextElementSibling
        : e.currentTarget.previousElementSibling;
    if (sib instanceof HTMLElement && sib.classList.contains("chat-item")) sib.focus();
  }

  // Demande la permission de notification une fois, à l'ouverture de la session.
  useEffect(() => {
    requestNotifyPermission();
  }, []);

  // Badge non-lus dans le titre de l'onglet. MÊME compte que les pastilles du rail
  // (Chats + E-mails) : INBOX actif uniquement — on exclut les archivées, les onglets
  // Gmail secondaires (Promotions/Social/Mises à jour) et le courrier hors INBOX.
  // Sinon le titre affichait un nombre énorme (newsletters/archivées) alors que rien
  // n'est non-lu à l'écran.
  useEffect(() => {
    const n = conversations.filter(
      (c) => !c.isArchived && convUnread(c) && !isSecondaryGmail(c) && !gmailKnownNotInbox(c),
    ).length;
    document.title = n > 0 ? `(${n}) Agency OS` : "Agency OS";
  }, [conversations]);

  // Raccourcis clavier : ⌘K (recherche), Échap (ferme/désélectionne), ⌘±/0 (zoom).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "k" || e.key === "K")) {
        // ⌘K = palette de commandes (overlay). La recherche de la barre haute
        // reste un filtre rapide de la liste, complémentaire.
        e.preventDefault();
        setPaletteOpen((v) => !v);
      } else if (e.key === "Escape") {
        if (paletteOpen) setPaletteOpen(false);
        else if (accountMenu) setAccountMenu(false);
        else if (showCompose) setShowCompose(false);
        else if (showSettings) setShowSettings(false);
        else if (query) setQuery("");
        else if (searchOpen) setSearchOpen(false);
        else if (selected) setSelected(null);
      } else if (mod && (e.key === "+" || e.key === "=")) {
        e.preventDefault();
        const z = clampZoom(loadPrefs().zoom + 0.1);
        applyZoom(z);
        savePrefs({ zoom: z });
      } else if (mod && e.key === "-") {
        e.preventDefault();
        const z = clampZoom(loadPrefs().zoom - 0.1);
        applyZoom(z);
        savePrefs({ zoom: z });
      } else if (mod && e.key === "0") {
        e.preventDefault();
        applyZoom(1);
        savePrefs({ zoom: 1 });
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showSettings, selected, query, searchOpen, showCompose, accountMenu, paletteOpen]);

  // Bouton retour natif (@capacitor/app — hardware/geste Android ; aucun
  // événement n'existe sur iOS, où la pile mobile liste→thread→infos s'appuie
  // sur le bouton ‹ retour visible). Dépile un niveau comme Échap : infos →
  // panneau/outil → fil ouvert → sinon on rend la main à l'OS (minimize).
  const onBackRef = useRef<() => boolean>(() => false);
  useEffect(() => {
    onBackRef.current = () => {
      if (showInfo) {
        setShowInfo(false);
        return true;
      }
      if (
        showCompose ||
        showSettings ||
        showHermes ||
        showProjects ||
        showCrm ||
        showAudit ||
        showMatches ||
        showNotifs
      ) {
        closePanels();
        return true;
      }
      if (selected) {
        setSelected(null);
        return true;
      }
      return false;
    };
  });
  useEffect(() => {
    const sub = CapApp.addListener("backButton", () => {
      if (!onBackRef.current()) {
        void CapApp.minimizeApp().catch(() => {
          /* web : pas de fenêtre native à réduire */
        });
      }
    });
    return () => {
      void sub
        .then((s) => s.remove())
        .catch(() => {
          /* déjà détaché */
        });
    };
  }, []);

  const loadConversations = useCallback(() => {
    messaging
      .conversations()
      .then((r) => setConversations(r.conversations))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "erreur"));
  }, []);

  // Compose : après un démarrage réussi, on recharge la liste et on ouvre la
  // nouvelle conversation si elle est déjà visible (le portail/thread peut
  // n'apparaître qu'au prochain /sync → sinon le polling/SSE la fera remonter).
  const onConversationStarted = useCallback(
    async (res: { conversationRemoteId: string; accountId: string }) => {
      setShowCompose(false);
      try {
        const r = await messaging.conversations();
        setConversations(r.conversations);
        const match = r.conversations.find(
          (c) => c.remoteId === res.conversationRemoteId && c.accountId === res.accountId,
        );
        if (match) openConversation(match);
      } catch {
        loadConversations();
      }
    },
    [loadConversations, openConversation],
  );

  useEffect(() => {
    loadConversations();
    const t = setInterval(loadConversations, 15000); // rafraîchit la liste (repli)
    return () => clearInterval(t);
  }, [loadConversations]);

  // Comptes (pour distinguer plusieurs espaces d'un même réseau dans la liste).
  // Best-effort : la liste reste utilisable sans (pas de label d'espace alors).
  useEffect(() => {
    accountsApi
      .list()
      .then((r) => setAccounts(r.accounts))
      .catch(() => setAccounts([]));
  }, []);
  // Synchronisation Gmail une fois par session de navigateur : le courrier ingéré
  // avant la capture des labels/lu a des données obsolètes → « Principal » ne
  // distinguait pas l'INBOX et le compteur de non-lus était gonflé vs Gmail. Au
  // 1ᵉʳ chargement avec un compte Gmail, on resynchronise TOUTES les conversations
  // (labels + lu/non-lu = miroir de Gmail) côté serveur, puis on recharge la liste.
  // Le live (poll) garde ensuite l'état lu à jour ; cette passe couvre l'historique.
  const relabeledRef = useRef(false);
  useEffect(() => {
    if (relabeledRef.current) return;
    if (!accounts.some((a) => netKey(a.network) === "gmail")) return;
    relabeledRef.current = true;
    try {
      if (sessionStorage.getItem("aos_gmail_synced_v6") === "1") return;
    } catch {
      /* sessionStorage indispo → on synchronise quand même */
    }
    // 1) backfill l'historique de l'inbox (threads absents), 2) relabel + état lu
    // de toutes les conversations, puis on recharge la liste. Best-effort.
    void (async () => {
      try {
        await accountsApi.gmailSyncInbox();
      } catch {
        /* le backfill peut échouer (connecteur arrêté) → le relabel suit quand même */
      }
      try {
        await accountsApi.gmailRelabel(true);
      } catch {
        /* best-effort : le live (poll) synchronisera au fil de l'eau */
      }
      try {
        sessionStorage.setItem("aos_gmail_synced_v6", "1");
      } catch {
        /* ignore */
      }
      loadConversations();
    })();
  }, [accounts, loadConversations]);
  // accountId → compte, et l'ensemble des réseaux où le membre a >1 compte : le
  // label d'espace ("Acme") n'est montré QUE dans ce cas, pour ne pas encombrer
  // la liste du cas mono-compte courant.
  const accountById = new Map(accounts.map((a) => [a.id, a]));
  const multiNets = new Set<string>();
  {
    const perNet = new Map<string, number>();
    for (const a of accounts) perNet.set(a.network, (perNet.get(a.network) ?? 0) + 1);
    for (const [net, n] of perNet) if (n > 1) multiNets.add(net);
  }
  const workspaceLabelOf = (c: Conversation): string | null => {
    if (!multiNets.has(c.network)) return null;
    const a = accountById.get(c.accountId);
    return a ? acctShortLabel(a) : null;
  };

  // Temps réel (#21/#22) : SSE → liste, fil ouvert, notifications, éphémères.
  const { live, typing, presence } = useRealtime({
    conversations,
    selectedId: selected?.id ?? null,
    loadConversations,
    openConversation,
    markConvRead,
    onReadChanged: (p) => {
      setConversations((prev) =>
        prev.map((c) =>
          c.accountId === p.accountId && c.remoteId === p.conversationRemoteId
            ? { ...c, unreadCount: p.unreadCount, isUnread: p.isUnread }
            : c,
        ),
      );
    },
  });

  async function signOut() {
    try {
      await auth.logout();
    } finally {
      onSignOut();
    }
  }

  // Bascule un flag local (optimiste) puis resynchronise depuis le serveur.
  async function setConvState(
    id: string,
    patch: { archived?: boolean; pinned?: boolean; unread?: boolean },
  ) {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              isArchived: patch.archived ?? c.isArchived,
              isPinned: patch.pinned ?? c.isPinned,
              isUnread: patch.unread ?? c.isUnread,
              unreadCount: patch.unread === false ? 0 : c.unreadCount,
            }
          : c,
      ),
    );
    try {
      await messaging.setState(id, patch);
    } catch {
      /* en cas d'échec, le prochain refresh (15 s) rétablit l'état réel */
    } finally {
      loadConversations();
    }
  }

  const q = query.trim().toLowerCase();
  // Le « monde » cloisonne la liste : e-mail d'un côté, chat de l'autre.
  const inWorld = (c: Conversation) =>
    world === "email" ? isEmailNetwork(c.network) : !isEmailNetwork(c.network);
  const worldConvs = conversations.filter(inWorld);
  const shown = worldConvs
    .filter((c) => {
      if (filter === "archived") return c.isArchived;
      if (c.isArchived) return false;
      // Filtres Gmail (onglet/label) : on cible UNE catégorie/label précis.
      if (filter.startsWith("gmail:")) {
        if (netKey(c.network) !== "gmail") return false;
        const token = filter.slice("gmail:".length);
        if (!(c.labels ?? []).includes(token)) return false;
        // Onglets catégorie = portée INBOX (comme Gmail) ; labels perso = partout.
        if (token.startsWith("category:")) return !gmailKnownNotInbox(c);
        return true;
      }
      if (filter === "unread")
        return convUnread(c) && !isSecondaryGmail(c) && !gmailKnownNotInbox(c);
      // Vue par réseau : la puce « Gmail » montre tout Gmail (boîte complète).
      if (filter !== "all") return netKey(c.network) === filter;
      // « Principal » : comme Gmail, l'INBOX uniquement — on masque les onglets
      // secondaires (Promotions/Social/…) ET le courrier hors INBOX (envoyé/
      // archivé). Accessibles via leurs filtres dédiés.
      return !isSecondaryGmail(c) && !gmailKnownNotInbox(c);
    })
    .filter(
      (c) =>
        !q ||
        (c.title || "").toLowerCase().includes(q) ||
        netInfo(c.network).label.toLowerCase().includes(q) ||
        (c.lastMessageBody || "").toLowerCase().includes(q),
    )
    .slice()
    .sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1; // épinglées en tête
      return (b.lastMessageAt ?? "").localeCompare(a.lastMessageAt ?? "");
    });

  // — Rail : un compteur de non-lus PAR MONDE (briques Chats / E-mails). —
  // Comme Gmail, la brique E-mails ne badge que l'INBOX PRIMAIRE : les onglets
  // secondaires (Promotions/Social/Mises à jour = newsletters) ne gonflent pas la
  // pastille. Les totaux dérivent de là (tab-bar mobile incluse).
  const notArchived = conversations.filter((c) => !c.isArchived);
  const unreadChats = notArchived.filter((c) => !isEmailNetwork(c.network) && convUnread(c)).length;
  const unreadEmails = notArchived.filter(
    (c) =>
      isEmailNetwork(c.network) && convUnread(c) && !isSecondaryGmail(c) && !gmailKnownNotInbox(c),
  ).length;
  const totalUnreadAll = unreadChats + unreadEmails;
  // Badge du centre de notifs = suggestions de fusion de contacts en attente.
  // Les non-lus vivent sur le badge de la brique Messagerie (pas de doublon ici).
  const notifCount = matchCount + pendingCount + threadNotifCount;
  // — Menu de vues : compteurs/filtres bornés au MONDE actif (chat vs e-mail). —
  const activeConvs = worldConvs.filter((c) => !c.isArchived);
  // Non-lus du chip « Tous »/« Principal » = ceux RÉELLEMENT visibles dans cette
  // vue. En e-mail, « Principal » = INBOX primaire → on exclut les onglets
  // secondaires (Promotions/Social/…) et le hors-INBOX, dont les non-lus sont
  // comptés sur LEURS chips. Pour le chat, ces filtres sont neutres (inchangé).
  const totalUnread = activeConvs.filter(
    (c) => convUnread(c) && !isSecondaryGmail(c) && !gmailKnownNotInbox(c),
  ).length;
  const archivedCount = worldConvs.filter((c) => c.isArchived).length;
  const unreadByNet: Record<string, number> = {};
  for (const c of activeConvs) {
    const k = netKey(c.network);
    if (k && convUnread(c)) unreadByNet[k] = (unreadByNet[k] ?? 0) + 1;
  }
  const presentNets = Array.from(
    new Set(activeConvs.map((c) => netKey(c.network)).filter((k): k is string => !!k)),
  );
  // Filtres Gmail (monde E-mails) : onglets non-Principal réellement présents
  // (dans l'ordre Gmail) + labels perso présents (triés). Rendus en chips à côté
  // de Tous / Non lus, façon onglets Gmail.
  const gmailConvs = activeConvs.filter((c) => netKey(c.network) === "gmail");
  // Onglet catégorie affiché seulement s'il existe un mail INBOX de cette catégorie
  // (comme Gmail : un onglet vide ne s'affiche pas).
  const presentGmailCategories = GMAIL_SECONDARY_CATEGORIES.filter((cat) =>
    gmailConvs.some((c) => gmailCategory(c) === cat && !gmailKnownNotInbox(c)),
  );
  const presentGmailLabels = Array.from(
    new Set(gmailConvs.flatMap((c) => (c.labels ?? []).filter((l) => l.startsWith("label:")))),
  ).sort((a, b) => a.localeCompare(b));
  // Titre de la liste = le monde actif (le filtrage fin passe par les chips).
  const worldLabel = world === "email" ? "E-mails" : "Chats";

  // Brique « Messagerie » active = aucune autre brique/outil ouvert (le canevas
  // par défaut affiche la messagerie). Pilote l'état actif du rail.
  const onMessaging =
    !showProjects &&
    !showCrm &&
    !showHermes &&
    !showSettings &&
    !showAudit &&
    !showCompose &&
    !showMatches &&
    !showNotifs;
  // Un panneau/outil est ouvert (≠ messagerie). Sur mobile, fait glisser la
  // colonne `.conversation` au-dessus de la liste (sinon les panneaux, rendus
  // dans `.conversation`, restaient hors écran : ils n'apparaissaient qu'avec une
  // conversation sélectionnée). La barre d'onglets mobile reste visible dessous.
  const anyPanel = !onMessaging;

  // Initiales du membre pour l'avatar du menu compte (fallback « ? »).
  const accountInitials = (me.displayName || me.email || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

  // Panneau infos (colonne droite) : seulement avec un fil ouvert, jamais par-dessus un outil.
  const threadView =
    !!selected &&
    !showAudit &&
    !showProjects &&
    !showCrm &&
    !showHermes &&
    !showMatches &&
    !showNotifs &&
    !showSettings &&
    !showCompose;
  const infoOpen = threadView && showInfo;
  // `selected` est figé à l'ouverture ; le tableau `conversations` est remplacé à
  // chaque refresh/SSE. On redérive l'état FRAIS de la conv ouverte par id → sinon
  // `selected.isPinned` reste périmé et le dés-épinglage renvoie toujours pinned:true (#4).
  const selectedLive = selected
    ? (conversations.find((c) => c.id === selected.id) ?? selected)
    : null;

  return (
    <div
      className={`app ${selected && onMessaging ? "chat-open" : ""} ${anyPanel ? "tool-open" : ""} ${infoOpen ? "info-open" : ""} ${showCrm ? "crm-open" : ""}`}
    >
      {/* Barre haute globale de la plateforme (desktop/tablette ; masquée < 700).
          Zone gauche = place des pastilles fenêtre macOS (titlebar masquée côté
          natif). Seule la recherche globale ⌘K y vit désormais : compose et
          recherche de liste sont dans l'en-tête de liste, le compte est en bas
          du rail, les outils transverses dans le menu compte. */}
      <header className="topbar">
        {/* Flèches précédent/suivant (#4). En colonne 1 de la grille : sur macOS,
            `.is-mac .topbar` réserve déjà 84px à gauche → les flèches tombent
            APRÈS les feux de la fenêtre, jamais dessous. */}
        <div className="topbar-nav">
          <button
            type="button"
            className="topbar-nav-btn"
            aria-label="Précédent"
            title="Précédent"
            disabled={!navState.canBack}
            onClick={navBack}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            className="topbar-nav-btn"
            aria-label="Suivant"
            title="Suivant"
            disabled={!navState.canFwd}
            onClick={navForward}
          >
            <ChevronRight size={18} />
          </button>
        </div>
        <button
          type="button"
          className="topbar-search"
          aria-label="Rechercher partout (⌘K)"
          onClick={() => setPaletteOpen(true)}
        >
          <Search size={15} aria-hidden="true" />
          <span className="topbar-search-ph">Rechercher partout…</span>
          <kbd className="topbar-kbd">⌘K</kbd>
        </button>
      </header>
      <nav className="rail" aria-label="Navigation principale">
        {/* Rail = BRIQUES de la plateforme uniquement (archi A) : on bascule
            entre destinations. Les futures briques (Agenda, To-do…) s'ajoutent
            ici comme un .rail-btn de plus. Les vues/filtres de la messagerie
            vivent dans l'en-tête de la liste (menu « Réception ⌄ ») ; les outils
            transverses (Projets/Hermes/Fusion/Réglages) passent par la palette
            ⌘K et le menu compte. */}
        <div className="rail-group rail-bricks">
          <button
            className={`rail-btn brick ${showNotifs ? "active" : ""}`}
            title="Notifications"
            aria-label="Notifications"
            onClick={() => navPanel("notifs")}
          >
            <Bell size={22} />
            {notifCount > 0 && (
              <span className="rail-badge">{notifCount > 99 ? "99+" : notifCount}</span>
            )}
          </button>
          <button
            className={`rail-btn brick ${onMessaging && world === "email" ? "active" : ""}`}
            title="E-mails"
            aria-label="E-mails"
            onClick={() => {
              switchWorld("email");
              navHome();
            }}
          >
            <Mail size={22} />
            {unreadEmails > 0 && (
              <span className="rail-badge">{unreadEmails > 99 ? "99+" : unreadEmails}</span>
            )}
          </button>
          <button
            className={`rail-btn brick ${onMessaging && world === "messaging" ? "active" : ""}`}
            title="Chats"
            aria-label="Chats"
            onClick={() => {
              switchWorld("messaging");
              navHome();
            }}
          >
            <MessagesSquare size={22} />
            {unreadChats > 0 && (
              <span className="rail-badge">{unreadChats > 99 ? "99+" : unreadChats}</span>
            )}
          </button>
          <button
            className={`rail-btn brick ${showCrm ? "active" : ""}`}
            title="Clients & contacts"
            aria-label="Clients et contacts"
            onClick={() => {
              setCrmCompanyName(null);
              setCrmPersonId(null);
              navPanel("crm");
            }}
          >
            <Contact size={22} />
          </button>
        </div>

        <div className="rail-spacer" />

        {/* Compte en bas du rail (desktop) : avatar à initiales → menu compte
            (identité + Réglages/Audit/Déconnexion). */}
        <div className="rail-group">
          <button
            type="button"
            className={`rail-btn account-btn ${accountMenu || showSettings || showAudit ? "active" : ""}`}
            title="Compte"
            aria-label="Compte"
            aria-haspopup="menu"
            aria-expanded={accountMenu}
            onClick={() => setAccountMenu((v) => !v)}
          >
            <span className="account-avatar">{accountInitials}</span>
          </button>
        </div>
      </nav>

      {/* Barre d'onglets MOBILE (< 700px) : équivalent tactile du rail allégé =
          Messages + Compte. Les outils transverses (Projets/Hermes/Fusion/
          Réglages) passent par la palette ⌘K et le menu compte. Masquée en
          desktop. Cachée quand un fil est ouvert (pattern iOS). */}
      <nav className="tabbar" aria-label="Sections">
        <button
          type="button"
          className={`tab ${onMessaging ? "active" : ""}`}
          onClick={navHome}
          aria-label="Messages"
        >
          <MessagesSquare size={22} />
          <span>Messages</span>
          {totalUnreadAll > 0 && (
            <span className="tab-badge">{totalUnreadAll > 99 ? "99+" : totalUnreadAll}</span>
          )}
        </button>
        <button
          type="button"
          className={`tab ${showNotifs ? "active" : ""}`}
          onClick={() => navPanel("notifs")}
          aria-label="Notifications"
        >
          <Bell size={22} />
          <span>Notifs</span>
          {notifCount > 0 && (
            <span className="tab-badge">{notifCount > 99 ? "99+" : notifCount}</span>
          )}
        </button>
        <button
          type="button"
          className={`tab ${accountMenu || showSettings || showAudit ? "active" : ""}`}
          onClick={() => setAccountMenu((v) => !v)}
          aria-label="Compte"
          aria-haspopup="menu"
          aria-expanded={accountMenu}
        >
          <span className="account-avatar tab-avatar">{accountInitials}</span>
          <span>Compte</span>
        </button>
      </nav>

      {/* Menu compte (niveau .app pour être visible en desktop ET mobile) :
          dropdown haut-droite en desktop, bottom-sheet au-dessus de la tabbar en
          mobile. Déclenché par l'avatar du header (desktop) ou l'onglet Compte. */}
      {accountMenu && (
        <>
          <div className="account-backdrop" onClick={() => setAccountMenu(false)} />
          <div className="account-menu" role="menu" aria-label="Menu du compte">
            <div className="account-id">
              <strong>{me.displayName || "Compte"}</strong>
              {me.email && <span>{me.email}</span>}
            </div>
            <div className="account-menu-sep" />
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setAccountMenu(false);
                navPanel("settings");
              }}
            >
              <SettingsIcon size={16} /> Réglages
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setAccountMenu(false);
                navPanel("audit");
              }}
            >
              <ScrollText size={16} /> Journal d'audit
            </button>
            <button
              type="button"
              role="menuitem"
              className="danger"
              onClick={() => {
                setAccountMenu(false);
                void signOut();
              }}
            >
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </>
      )}

      <section
        className={`chat-list ${prefs.compactList ? "compact" : ""} ${prefs.showPreviews ? "" : "no-previews"}`}
      >
        <div className="list-head">
          <strong className="list-head-title">{worldLabel}</strong>
          <div className="list-head-actions">
            <button
              title="Rechercher"
              aria-label="Rechercher une conversation"
              className={searchOpen ? "active" : ""}
              onClick={() => {
                setSearchOpen((v) => !v);
                requestAnimationFrame(() => searchRef.current?.focus());
              }}
            >
              <Search size={18} />
            </button>
            <button
              title="Nouveau message"
              aria-label="Nouveau message"
              onClick={() => {
                closePanels();
                setShowCompose(true);
              }}
            >
              <ComposeIcon size={18} />
            </button>
          </div>
        </div>
        {/* #7 — Filtres en onglets-chips façon WhatsApp (Tous / Non lus / par
            réseau / Archivées) : accès direct, sans menu déroulant, identique en
            desktop et mobile. */}
        <div className="list-filters" role="tablist" aria-label="Filtres de la messagerie">
          <button
            role="tab"
            aria-selected={filter === "all"}
            className={`list-filter-chip ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            {/* Monde E-mails : « Tous » ne montre que le Principal (les onglets
                secondaires ont leurs chips), donc on l'intitule « Principal ». */}
            {world === "email" ? "Principal" : "Tous"}
            {totalUnread > 0 && <span className="chip-badge">{totalUnread}</span>}
          </button>
          <button
            role="tab"
            aria-selected={filter === "unread"}
            className={`list-filter-chip ${filter === "unread" ? "active" : ""}`}
            onClick={() => setFilter("unread")}
          >
            Non lus
          </button>
          {/* Chips par réseau seulement si le monde en compte plusieurs : un seul
              réseau (ex. Gmail dans le monde E-mails) rend le chip redondant. */}
          {presentNets.length > 1 &&
            presentNets.map((k) => (
              <button
                key={k}
                role="tab"
                aria-selected={filter === k}
                className={`list-filter-chip ${filter === k ? "active" : ""}`}
                onClick={() => setFilter(k)}
              >
                <NetIcon network={k} size={14} /> {netInfo(k).label}
                {(unreadByNet[k] ?? 0) > 0 && <span className="chip-badge">{unreadByNet[k]}</span>}
              </button>
            ))}
          {/* Onglets Gmail (Promotions/Social/Mises à jour/Forums) — comme dans
              Gmail, accessibles depuis « Tous » qui ne montre que le Principal. */}
          {presentGmailCategories.map((cat) => {
            const f = `gmail:category:${cat}`;
            // Non-lus de l'onglet (mail INBOX de cette catégorie) → à traiter.
            const n = gmailConvs.filter(
              (c) => gmailCategory(c) === cat && !gmailKnownNotInbox(c) && convUnread(c),
            ).length;
            return (
              <button
                key={f}
                role="tab"
                aria-selected={filter === f}
                className={`list-filter-chip ${filter === f ? "active" : ""}`}
                onClick={() => setFilter(f)}
              >
                {GMAIL_CATEGORY_LABELS[cat]}
                {n > 0 && <span className="chip-badge">{n}</span>}
              </button>
            );
          })}
          {/* Labels perso Gmail. */}
          {presentGmailLabels.map((token) => {
            const f = `gmail:${token}`;
            const n = gmailConvs.filter(
              (c) => (c.labels ?? []).includes(token) && convUnread(c),
            ).length;
            return (
              <button
                key={f}
                role="tab"
                aria-selected={filter === f}
                className={`list-filter-chip ${filter === f ? "active" : ""}`}
                onClick={() => setFilter(f)}
              >
                <Ic name="tag" size={13} /> {token.slice("label:".length)}
                {n > 0 && <span className="chip-badge">{n}</span>}
              </button>
            );
          })}
          {archivedCount > 0 && (
            <button
              role="tab"
              aria-selected={filter === "archived"}
              className={`list-filter-chip ${filter === "archived" ? "active" : ""}`}
              onClick={() => setFilter("archived")}
            >
              Archivées
            </button>
          )}
        </div>
        <div className={`search list-search ${searchOpen ? "open" : ""}`}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          >
            <circle cx="10.5" cy="10.5" r="7" />
            <path d="m20 20-4.2-4.2" />
          </svg>
          <input
            ref={searchRef}
            placeholder="Rechercher…"
            aria-label="Rechercher une conversation"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              className="clear"
              aria-label="Effacer la recherche"
              onClick={() => setQuery("")}
            >
              ✕
            </button>
          )}
        </div>
        {error && (
          <div className="error mobile-error" aria-live="polite" onClick={() => setError(null)}>
            {error}
          </div>
        )}

        {shown.map((c) => (
          <div
            key={c.id}
            className={`chat-item ${selected?.id === c.id ? "selected" : ""} ${convUnread(c) ? "unread" : ""}`}
            onClick={() => openConversation(c)}
            onKeyDown={(e) => onChatItemKeys(e, c)}
            role="button"
            tabIndex={0}
            aria-label={`${rowTitle(c)}${convUnread(c) ? " — non lu" : ""}`}
          >
            <div className="chat-row-main">
              <Avatar chat={c} size={40} name={rowTitle(c)} />
              <div className="chat-meta">
                {/* Ligne 1 : nom + heure — ligne 2 : aperçu + badge (REFERENCE §4) */}
                <div className="chat-row">
                  <span className="chat-title">{rowTitle(c)}</span>
                  {workspaceLabelOf(c) && (
                    <span className="chat-acct" title={workspaceLabelOf(c)!}>
                      {workspaceLabelOf(c)}
                    </span>
                  )}
                  {c.isPinned && (
                    <span className="pin-flag" title="Épinglée">
                      <Ic name="pin" size={11} />
                    </span>
                  )}
                  <span className="chat-time">{fmtTime(c.lastMessageAt)}</span>
                </div>
                <div className="chat-row">
                  <span className="chat-preview">{rowPreview(c)}</span>
                  {c.unreadCount > 0 ? (
                    <span className="badge">{c.unreadCount}</span>
                  ) : c.isUnread ? (
                    <span className="badge badge-dot" />
                  ) : null}
                </div>
              </div>
            </div>
            <span className="quick-actions" onClick={(e) => e.stopPropagation()}>
              <button
                className="quick-read"
                title={convUnread(c) ? "Marquer lu" : "Marquer non lu"}
                aria-label={convUnread(c) ? "Marquer lu" : "Marquer non lu"}
                onClick={() =>
                  convUnread(c) ? markConvRead(c.id) : setConvState(c.id, { unread: true })
                }
              >
                {convUnread(c) ? "✓" : <Ic name="unread" size={16} />}
              </button>
              <button
                className="quick-read"
                title={c.isPinned ? "Désépingler" : "Épingler"}
                aria-label={c.isPinned ? "Désépingler" : "Épingler"}
                onClick={() => setConvState(c.id, { pinned: !c.isPinned })}
              >
                <Ic name="pin" size={16} />
              </button>
              <button
                className="quick-read"
                title={c.isArchived ? "Désarchiver" : "Archiver"}
                aria-label={c.isArchived ? "Désarchiver" : "Archiver"}
                onClick={() => setConvState(c.id, { archived: !c.isArchived })}
              >
                <Ic name="archive" size={16} />
              </button>
            </span>
          </div>
        ))}
        {shown.length === 0 && (
          <p className="empty">
            {query
              ? "Aucun résultat"
              : filter === "archived"
                ? "Aucune conversation archivée"
                : "Aucune conversation"}
          </p>
        )}
      </section>
      <main
        className="conversation"
        // Cliquer dans le fil referme la colonne infos (#3). On exclut l'en-tête
        // (`.conv-head`), qui garde son propre toggle infos — sinon double bascule.
        onClickCapture={
          infoOpen
            ? (e) => {
                if (!(e.target as HTMLElement).closest(".conv-head")) setShowInfo(false);
              }
            : undefined
        }
      >
        {/* Fallback minimal : les chunks lazy pèsent quelques Ko, le spinner ne
            flashe qu'au premier accès à l'écran concerné. */}
        <Suspense fallback={<PageLoader />}>
          {showCompose ? (
            <ComposePane onStarted={onConversationStarted} onClose={() => setShowCompose(false)} />
          ) : showAudit ? (
            <Audit onClose={() => setShowAudit(false)} />
          ) : showProjects ? (
            <Projects onClose={() => setShowProjects(false)} />
          ) : showCrm ? (
            <Crm
              onClose={() => setShowCrm(false)}
              openCompanyName={crmCompanyName}
              openPersonId={crmPersonId}
              onOpenConversation={openConversationById}
              onMessage={async (handle, accountId) => {
                setShowCrm(false);
                try {
                  const res = await messaging.startConversation(handle, accountId);
                  await onConversationStarted(res);
                } catch {
                  /* réseau indispo : on retombe sur l'écran courant */
                }
              }}
            />
          ) : showHermes ? (
            <Hermes
              conversations={conversations}
              onClose={() => setShowHermes(false)}
              onOpen={(c) => openConversation(c)}
            />
          ) : showMatches ? (
            <MatchCenter onClose={() => setShowMatches(false)} onChanged={refreshMatchCount} />
          ) : showNotifs ? (
            <NotificationCenter
              onOpenMatches={() => navPanel("matches")}
              onOpenContact={(personId) => {
                setCrmCompanyName(null);
                setCrmPersonId(personId);
                navPanel("crm");
              }}
              onOpenThread={openThreadFromNotif}
              onMatchesChanged={refreshNotifCounts}
              onClose={() => setShowNotifs(false)}
            />
          ) : selectedLive ? (
            <Thread
              key={selectedLive.id}
              conversation={selectedLive}
              live={live}
              typing={typing}
              presence={presence}
              openThreadRootRemoteId={
                pendingThread && pendingThread.convId === selectedLive.id
                  ? pendingThread.rootRemoteId
                  : null
              }
              onThreadOpened={() => setPendingThread(null)}
              onToggleInfo={() => setShowInfo((v) => !v)}
              onBack={() => setSelected(null)}
            />
          ) : (
            <div className="placeholder">
              <EmptyState icon={<MessagesSquare size={44} />}>
                Sélectionne une conversation
              </EmptyState>
            </div>
          )}
        </Suspense>
      </main>
      {infoOpen && selectedLive && (
        <InfoPanel
          key={selectedLive.id}
          conversation={selectedLive}
          pinned={!!selectedLive.isPinned}
          onPin={() => setConvState(selectedLive.id, { pinned: !selectedLive.isPinned })}
          onClose={() => setShowInfo(false)}
          onOpenContact={(personId) => {
            setCrmCompanyName(null);
            setCrmPersonId(personId);
            navPanel("crm");
          }}
        />
      )}
      {showSettings && (
        <Suspense fallback={null}>
          <Settings
            me={me}
            prefs={prefs}
            onPrefs={updatePrefs}
            onClose={() => setShowSettings(false)}
          />
        </Suspense>
      )}
      {paletteOpen && (
        <CommandPalette
          onClose={() => setPaletteOpen(false)}
          commands={paletteCommands}
          conversations={conversations}
          onOpenConversation={openConversation}
        />
      )}
    </div>
  );
}
